import React, { useState, useEffect, useRef } from "react";
import flowerImage from "../../assets/flower.png";

const CursorEffect = () => {
  const [particles, setParticles] = useState([]);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorRotation, setCursorRotation] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef();
  const particleIdRef = useRef(0);
  const rotationSpeedRef = useRef(0);
  const movementTimeoutRef = useRef();

  // Mouse move handler
  useEffect(() => {
    // Particle creation function
    const createParticle = (x, y) => {
      const angle = Math.random() * Math.PI * 2;
      const velocity = Math.random() * 1.2; // Random velocity between 0.7-2.1 (70% of previous)

      // Size variation: 80% chance for small flowers, 20% chance for big flowers
      const size =
        Math.random() < 0.8
          ? 3 + Math.random() * 2 // Small flowers: 3-5px
          : 9 + Math.random() * 6; // Big flowers: 9-15px

      return {
        id: particleIdRef.current++,
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size,
        age: 0,
        maxAge: 1.5, // 1.5 seconds lifetime
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 4, // Random rotation speed
      };
    };

    const handleMouseMove = (e) => {
      const currentPos = { x: e.clientX, y: e.clientY };
      const lastPos = lastMousePositionRef.current;

      // Update cursor position
      setCursorPosition(currentPos);

      // Calculate movement distance
      const distance = Math.sqrt(
        Math.pow(currentPos.x - lastPos.x, 2) +
          Math.pow(currentPos.y - lastPos.y, 2)
      );

      // Update rotation based on movement speed
      if (distance > 0) {
        setIsMoving(true);
        rotationSpeedRef.current = Math.min(distance * 2, 15); // Cap rotation speed

        // Clear previous timeout
        if (movementTimeoutRef.current) {
          clearTimeout(movementTimeoutRef.current);
        }

        // Switch to idle rotation after 150ms of no movement
        movementTimeoutRef.current = setTimeout(() => {
          setIsMoving(false);
          rotationSpeedRef.current = 1; // Gentle idle rotation
        }, 150);
      }

      // Only emit particles if mouse moved more than threshold
      if (distance > 5) {
        mousePositionRef.current = currentPos;

        // Create 0-1 particles per movement (much more subtle)
        const newParticles = [];
        if (Math.random() < 0.4) {
          // 40% chance to spawn a particle
          newParticles.push(createParticle(currentPos.x, currentPos.y));
        }

        setParticles((prev) => {
          const updated = [...prev, ...newParticles];
          // Limit max particles to 15
          return updated.slice(-15);
        });

        lastMousePositionRef.current = currentPos;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Initialize cursor position and idle rotation
  useEffect(() => {
    // Start with gentle idle rotation
    rotationSpeedRef.current = 1;

    const initializeCursor = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
      window.removeEventListener("mousemove", initializeCursor);
    };

    window.addEventListener("mousemove", initializeCursor);
    return () => window.removeEventListener("mousemove", initializeCursor);
  }, []);

  // Animation loop
  useEffect(() => {
    const animate = () => {
      // Update cursor rotation (always rotate if speed > 0)
      if (rotationSpeedRef.current > 0) {
        setCursorRotation((prev) => prev + rotationSpeedRef.current);
      }

      setParticles((prev) => {
        const deltaTime = 0.016; // ~60fps

        return prev
          .map((particle) => ({
            ...particle,
            x: particle.x + particle.vx,
            y: particle.y + particle.vy,
            vx: particle.vx * 0.98, // Slow down over time
            vy: particle.vy * 0.98 + 0.1, // Add slight gravity
            age: particle.age + deltaTime,
            rotation: particle.rotation + particle.rotationSpeed,
          }))
          .filter((particle) => particle.age < particle.maxAge); // Remove expired particles
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (movementTimeoutRef.current) {
        clearTimeout(movementTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      {/* Rotating cursor */}
      <img
        src={flowerImage}
        alt=""
        style={{
          position: "absolute",
          left: cursorPosition.x - 8,
          top: cursorPosition.y - 8,
          width: 16,
          height: 16,
          transform: `rotate(${cursorRotation}deg)`,
          pointerEvents: "none",
          userSelect: "none",
          transition: isMoving ? "none" : "transform 0.3s ease-out",
        }}
      />

      {particles.map((particle) => {
        const progress = particle.age / particle.maxAge;
        const opacity = 0.8 * (1 - progress); // Fade out over time

        return (
          <img
            key={particle.id}
            src={flowerImage}
            alt=""
            style={{
              position: "absolute",
              left: particle.x - particle.size / 2,
              top: particle.y - particle.size / 2,
              width: particle.size,
              height: particle.size,
              opacity,
              transform: `rotate(${particle.rotation}deg)`,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        );
      })}
    </div>
  );
};

export default CursorEffect;
